import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Plus, Trash2, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { billsService } from '@/services/billsService'
import { accountManagementService, AccountSettingResponse } from '@/services/accountManagementService'
import {
  BillVm,
  BillLine,
  BillVendor,
  BillProject,
  BillItem,
  BillTaxRate,
  CostLocation,
  ProfitCenter,
  Department,
  BillAccount,
} from '@/api/types'

export const BillsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  // Helper function to find 15% VAT tax rate (prioritize taxRateId: 1)
  const getDefaultTaxRate = (taxRates: BillTaxRate[]): { taxRateId: number; taxRate: number } | null => {
    // First, try to find taxRateId === 1
    const taxById = taxRates.find(t => (t.taxRateId || t.id) === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId || taxById.id || 0,
        taxRate: taxById.taxPercent || taxById.taxRate || 0
      }
    }
    // Fallback: find by 15% rate
    const tax15 = taxRates.find(t => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01 // Allow small floating point differences
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId || tax15.id || 0,
        taxRate: tax15.taxPercent || tax15.taxRate || 0
      }
    }
    return null
  }

  const getDefaultRows = (taxRates: BillTaxRate[] = []): BillLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return Array.from({ length: 1 }, (): BillLine => ({
      lineId: 0,
      billId: 0,
      itemId: 0,
      description: '',
      qty: 0,
      unitPrice: 0,
      itemPrice: 0,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 0,
      taxRateId: defaultTax?.taxRateId || 0,
      taxRate: defaultTax?.taxRate || 0,
      taxAmount: 0,
      lineAmount: 0,
      debitAccount: 0,
      departmentId: 0,
      costProfitCenterId: 0,
      costLocationId: 0,
      taxPercent: defaultTax?.taxRate || 0,
      isDeleted: 'false',
    }))
  }

  const [bill, setBill] = useState<BillVm>({
    billId: 0,
    billDate: new Date().toISOString().split('T')[0],
    vendorId: 0,
    projectId: 0,
    dueDate: '',
    subTotal: 0,
    totalTax: 0,
    total: 0,
    note: '',
    branchId: branchId || 0,
    vendorBillDate: '',
    ipAddress: '',
    retentionPercent: 0,
    totalRetentionAmount: 0,
    billLines: getDefaultRows([]),
  })

  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [projects, setProjects] = useState<BillProject[]>([])
  const [items, setItems] = useState<BillItem[]>([])
  const [taxRates, setTaxRates] = useState<BillTaxRate[]>([])
  const [costLocations, setCostLocations] = useState<CostLocation[]>([])
  const [profitCenters, setProfitCenters] = useState<ProfitCenter[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [accounts, setAccounts] = useState<BillAccount[]>([])
  const [destinationAccount, setDestinationAccount] = useState<number>(0)
  const [vendorModuleId, setVendorModuleId] = useState<number | null>(null)
  const [accountSetting, setAccountSetting] = useState<AccountSettingResponse | null>(null)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  // Load finance modules to find Vendor/Payment module
  useEffect(() => {
    const loadFinanceModules = async () => {
      try {
        // Vendor module ID is 398 (as per user requirement)
        const VENDOR_MODULE_ID = 398
        setVendorModuleId(VENDOR_MODULE_ID)
        
        console.log('🔵 [BillsDetail] Vendor Module ID set to:', VENDOR_MODULE_ID)
      } catch (error: any) {
        console.error('Error loading finance modules:', error)
        // Even if error, set the module ID directly
        const VENDOR_MODULE_ID = 398
        setVendorModuleId(VENDOR_MODULE_ID)
      }
    }
    loadFinanceModules()
  }, [])

  // Load account setting for vendor module
  useEffect(() => {
    if (vendorModuleId && branchId) {
      const loadAccountSetting = async () => {
        try {
          const setting = await accountManagementService.getAccountSettingById(vendorModuleId, branchId)
          setAccountSetting(setting)
        } catch (error: any) {
          console.error('Error loading account setting:', error)
        }
      }
      loadAccountSetting()
    }
  }, [vendorModuleId, branchId])

  // Load all lookup data
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [
          vendorsData,
          projectsData,
          itemsData,
          taxRatesData,
          costLocationsData,
          profitCentersData,
          departmentsData,
          accountsData,
        ] = await Promise.all([
          billsService.getAllVendors(branchId),
          billsService.getProjects(branchId),
          billsService.getItems(branchId),
          billsService.getTaxRates(branchId),
          billsService.getCostLocations(),
          billsService.getProfitCenters(),
          billsService.getDepartments(),
          billsService.getAllAccounts(branchId),
        ])

        setVendors(vendorsData)
        setProjects(projectsData)
        setItems(itemsData)
        setTaxRates(taxRatesData)
        setCostLocations(costLocationsData)
        setProfitCenters(profitCentersData)
        setDepartments(departmentsData)
        
        console.log('🔵 [BillsDetail] Accounts Data:', accountsData)
        console.log('🔵 [BillsDetail] Accounts Count:', accountsData.length)
        setAccounts(accountsData)
      } catch (error: any) {
        console.error('Failed to load lookups', error)
        await showValidationError('Failed to load lookup data')
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax) {
      console.warn('Default tax rate (15% or ID 1) not found in taxRates:', taxRates)
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setBill(prev => {
      const hasRowsWithoutVAT = prev.billLines?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.billLines?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
            taxPercent: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, billLines: updatedLines }
    })
  }, [taxRates])

  // Auto-fill destination account when vendor is selected
  useEffect(() => {
    if (bill.vendorId && accountSetting && accountSetting.accountTemplate) {
      // TransactionTypeId for Vendor is 398 (as per user requirement)
      const TRANSACTION_TYPE_ID = 398
      
      console.log('🔵 [BillsDetail] Looking for transactionTypeIdD:', TRANSACTION_TYPE_ID)
      console.log('🔵 [BillsDetail] Account Templates:', accountSetting.accountTemplate)
      
      // Find the template with transactionTypeIdD = 398
      const template = accountSetting.accountTemplate.find(
        (t) => t.transactionTypeIdD === TRANSACTION_TYPE_ID
      )
      
      if (template && template.creditAccountId) {
        console.log('🔵 [BillsDetail] Found credit account:', template.creditAccountId, 'for transactionTypeIdD:', TRANSACTION_TYPE_ID)
        setDestinationAccount(template.creditAccountId)
      } else {
        console.warn('⚠️ [BillsDetail] No template found for transactionTypeIdD:', TRANSACTION_TYPE_ID)
        // Fallback to first template if available
        const firstTemplate = accountSetting.accountTemplate[0]
        if (firstTemplate && firstTemplate.creditAccountId) {
          console.log('🔵 [BillsDetail] Using first template credit account:', firstTemplate.creditAccountId)
          setDestinationAccount(firstTemplate.creditAccountId)
        }
      }
    }
  }, [bill.vendorId, accountSetting])

  // Load bill data for edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadBill = async () => {
      try {
        setLoading(true)
        const billId = Number(id)
        console.log('🟢 [BillsDetail] Loading bill for edit:', { billId, branchId })
        
        const billData = await billsService.getBillById(branchId, billId)
        console.log('🟢 [BillsDetail] Received bill data:', billData)
        console.log('🟢 [BillsDetail] Bill lines count:', billData.billLines?.length || 0)
        console.log('🟢 [BillsDetail] Bill lines:', billData.billLines)

        // Format dates
        const formatDateForInput = (dateString?: string) => {
          if (!dateString) return ''
          try {
            if (dateString.includes('T')) {
              return new Date(dateString).toISOString().split('T')[0]
            }
            return dateString.split('T')[0]
          } catch {
            return dateString
          }
        }

        // Use billLines if it exists and has items, otherwise use default rows only if it's truly empty/undefined
        const finalBillLines = (billData.billLines && billData.billLines.length > 0) 
          ? billData.billLines 
          : getDefaultRows()

        console.log('🟢 [BillsDetail] Final bill lines to set:', finalBillLines.length, 'items')
        console.log('🟢 [BillsDetail] Bill data before setting state:', {
          billId: billData.billId,
          vendorId: billData.vendorId,
          projectId: billData.projectId,
          billDate: billData.billDate,
          vendorBillDate: billData.vendorBillDate,
          dueDate: billData.dueDate,
          note: billData.note,
          subTotal: billData.subTotal,
          totalTax: billData.totalTax,
          total: billData.total,
        })

        const billStateToSet = {
          ...billData,
          billDate: formatDateForInput(billData.billDate) || new Date().toISOString().split('T')[0],
          vendorBillDate: formatDateForInput(billData.vendorBillDate) || formatDateForInput(billData.billDate) || '',
          dueDate: formatDateForInput(billData.dueDate) || '',
          billLines: finalBillLines,
        }

        console.log('🟢 [BillsDetail] Bill state to set:', billStateToSet)
        console.log('🟢 [BillsDetail] Vendor ID:', billStateToSet.vendorId)
        console.log('🟢 [BillsDetail] Project ID:', billStateToSet.projectId)
        console.log('🟢 [BillsDetail] Bill Date:', billStateToSet.billDate)
        console.log('🟢 [BillsDetail] Vendor Bill Date:', billStateToSet.vendorBillDate)
        console.log('🟢 [BillsDetail] Due Date:', billStateToSet.dueDate)

        setBill(billStateToSet)

        console.log('🟢 [BillsDetail] Bill state updated successfully')

      } catch (error: any) {
        console.error('❌ [BillsDetail] Failed to load bill', error)
        console.error('❌ [BillsDetail] Error details:', error.response?.data)
        await showSaveError(error, true)
      } finally {
        setLoading(false)
      }
    }

    // Load bill in edit mode - don't wait for vendors
    // Vendors will be loaded separately and form will update when they're available
    loadBill()
  }, [isEditMode, branchId, id])

  // Calculate line totals
  const calculateLineTotals = (line: BillLine): BillLine => {
    const qty = Number(line.qty) || 0
    const unitPrice = Number(line.unitPrice) || 0
    const discountPercent = Number(line.discountPercent) || 0
    const taxPercent = Number(line.taxPercent) || Number(line.taxRate) || 0

    const itemPrice = qty * unitPrice
    const discountAmount = itemPrice * (discountPercent / 100)
    const taxableAmount = itemPrice - discountAmount
    const taxAmount = (taxableAmount * taxPercent) / 100
    const lineAmount = taxableAmount + taxAmount

    return {
      ...line,
      itemPrice,
      discountAmount,
      taxableAmount,
      taxAmount,
      lineAmount,
      taxRate: taxPercent,
    }
  }

  // Calculate totals
  const totals = useMemo(() => {
    const validLines = bill.billLines?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const subTotal = validLines.reduce((sum, line) => sum + (Number(line.itemPrice) || 0), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (Number(line.discountAmount) || 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (Number(line.taxableAmount) || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (Number(line.taxAmount) || 0), 0)
    
    // Total = SubTotal - Discount + VAT
    const total = subTotal - totalDiscount + totalTax

    return { subTotal, totalDiscount, taxableAmount, totalTax, totalRetentionAmount: 0, total }
  }, [bill.billLines, deletedRowIndices])

  // Update bill totals when lines change
  useEffect(() => {
    setBill((prev) => ({
      ...prev,
      subTotal: totals.subTotal,
      totalTax: totals.totalTax,
      totalRetentionAmount: totals.totalRetentionAmount,
      total: totals.total,
    }))
  }, [totals])

  // Handle vendor change
  const handleVendorChange = (value: string) => {
    const vendorId = Number(value) || 0
    setBill((prev) => ({ ...prev, vendorId }))
  }

  // Handle line change
  const handleLineChange = (index: number, field: keyof BillLine, value: any) => {
    setBill((prev) => {
      const newLines = [...(prev.billLines || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      // Recalculate if price/quantity/tax/discount changes
      if (field === 'qty' || field === 'unitPrice' || field === 'taxRateId' || field === 'taxPercent' || field === 'discountPercent') {
        if (field === 'taxRateId' && value) {
          const taxRate = taxRates.find((t) => (t.taxRateId || t.id) === Number(value))
          if (taxRate) {
            updatedLine.taxPercent = taxRate.taxPercent || taxRate.taxRate || 0
            updatedLine.taxRate = taxRate.taxPercent || taxRate.taxRate || 0
          }
        }
        const recalculated = calculateLineTotals(updatedLine)
        newLines[index] = recalculated
      } else {
        newLines[index] = updatedLine
      }

      return { ...prev, billLines: newLines }
    })
  }

  // Add rows
  const handleAddRows = (count: number) => {
    setBill((prev) => {
      const defaultTax = getDefaultTaxRate(taxRates)
      if (defaultTax) {
        console.log('Adding rows with default VAT:', defaultTax)
      } else {
        console.warn('No default tax rate found when adding rows. TaxRates:', taxRates)
      }
      const newLines: BillLine[] = Array.from({ length: count }, (): BillLine => ({
        lineId: 0,
        billId: 0,
        itemId: 0,
        description: '',
        qty: 0,
        unitPrice: 0,
        itemPrice: 0,
        discountPercent: 0,
        discountAmount: 0,
        taxableAmount: 0,
        taxRateId: defaultTax?.taxRateId || 0,
        taxRate: defaultTax?.taxRate || 0,
        taxAmount: 0,
        lineAmount: 0,
        debitAccount: 0,
        departmentId: 0,
        costProfitCenterId: 0,
        costLocationId: 0,
        taxPercent: defaultTax?.taxRate || 0,
        isDeleted: 'false',
      }))
      return { ...prev, billLines: [...(prev.billLines || []), ...newLines] }
    })
  }

  // Delete row
  const handleDeleteRow = (index: number) => {
    const line = bill.billLines?.[index]
    if (line?.lineId && line.lineId > 0) {
      setDeletedRowIndices((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    } else {
      setBill((prev) => {
        const newLines = [...(prev.billLines || [])]
        newLines.splice(index, 1)
        return { ...prev, billLines: newLines }
      })
    }
  }

  // Format date for input
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    } catch {
      return dateString
    }
  }

  // Get user IP address (logged-in user's IP, not server IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  // Save bill
  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('bills.pleaseSelectBranch'))
      return
    }

    if (!bill.vendorId) {
      await showValidationError(t('bills.pleaseSelectVendor'))
      return
    }

    if (!bill.billLines || bill.billLines.length === 0) {
      await showValidationError(t('bills.pleaseAddAtLeastOneItem'))
      return
    }

    const validLines = bill.billLines.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      return !isDeleted && line.itemId && line.itemId > 0
    })

    if (validLines.length === 0) {
      await showValidationError(t('bills.pleaseAddAtLeastOneValidItem'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? t('bills.updatingBill') : t('bills.savingBill'),
        t('bills.pleaseWait')
      )

      // Get user IP address (logged-in user's IP, not server IP)
      const ipAddress = await getUserIPAddress()

      const linesToSave: BillLine[] = []
      bill.billLines?.forEach((line, index) => {
        const isDeleted = deletedRowIndices.has(index)

        // For non-deleted lines: only save if they have actual data (itemId > 0)
        // Empty lines without itemId should not be saved
        if (!isDeleted) {
          // Skip empty lines - only save lines that have itemId set
          if (!line.itemId || line.itemId === 0) {
            return // Skip this empty line
          }
        }

        const recalculatedLine = calculateLineTotals(line)

        // Create line matching exact API structure (without transactionTypeIdD)
        const lineToSave: BillLine = {
          lineId: recalculatedLine.lineId || 0,
          billId: bill.billId || 0,
          itemId: recalculatedLine.itemId || 0,
          description: recalculatedLine.description || '',
          qty: recalculatedLine.qty || 0,
          unitPrice: recalculatedLine.unitPrice || 0,
          itemPrice: recalculatedLine.itemPrice || 0,
          discountPercent: recalculatedLine.discountPercent || 0,
          discountAmount: recalculatedLine.discountAmount || 0,
          taxableAmount: recalculatedLine.taxableAmount || 0,
          taxRateId: recalculatedLine.taxRateId || 0,
          taxRate: recalculatedLine.taxRate || 0,
          taxAmount: recalculatedLine.taxAmount || 0,
          lineAmount: recalculatedLine.lineAmount || 0,
          debitAccount: recalculatedLine.debitAccount || 0,
          departmentId: recalculatedLine.departmentId || 0,
          costProfitCenterId: recalculatedLine.costProfitCenterId || 0,
          costLocationId: recalculatedLine.costLocationId || 0,
          taxPercent: recalculatedLine.taxPercent || 0,
          isDeleted: isDeleted ? 'true' : 'false',
        }
        
        linesToSave.push(lineToSave)
      })

      const toApiDate = (value?: string): string => {
        if (!value) return new Date().toISOString()
        try {
          if (value.includes('T')) {
            return new Date(value).toISOString()
          }
          return new Date(`${value}T00:00:00`).toISOString()
        } catch {
          return new Date().toISOString()
        }
      }

      // Create payload matching exact API structure
      const payload: BillVm = {
        billId: bill.billId || 0,
        billDate: toApiDate(bill.billDate),
        vendorId: bill.vendorId,
        projectId: bill.projectId || 0,
        dueDate: toApiDate(bill.dueDate),
        subTotal: totals.subTotal,
        totalTax: totals.totalTax,
        total: totals.total,
        note: bill.note || '',
        branchId: branchId,
        vendorBillDate: toApiDate(bill.vendorBillDate) || toApiDate(bill.billDate),
        ipAddress: ipAddress, // Set logged-in user's IP address
        retentionPercent: 0,
        totalRetentionAmount: 0,
        billLines: linesToSave,
      }

      // Log payload to console in exact API structure format
      console.log('%c=== Bill Save Payload ===', 'background: #FF5722; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
      console.log('%cIs Edit Mode:', 'color: #FF9800; font-weight: bold;', isEditMode)
      console.log('%cAPI Endpoint:', 'color: #4CAF50; font-weight: bold;', isEditMode ? 'PUT /api/ApiBills/Update' : 'POST /api/ApiBills/Insert')
      console.log('%cIP Address (User):', 'color: #2196F3; font-weight: bold;', ipAddress)
      console.log('%cPayload (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(payload, null, 2))
      console.log('%cPayload (Object):', 'color: #009688; font-weight: bold;', payload)
      console.log('%cBill Lines:', 'color: #795548; font-weight: bold;', linesToSave)
      console.log('%cBill Lines JSON:', 'color: #607D8B; font-weight: bold;', JSON.stringify(linesToSave, null, 2))
      console.log('%c===================================', 'background: #FF5722; color: white; padding: 5px; font-weight: bold;')

      if (isEditMode) {
        await billsService.updateBill(payload)
      } else {
        await billsService.saveBill(payload)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(isEditMode, isEditMode ? 'Bill updated successfully' : 'Bill created successfully')

      navigate('/purchases/bills')
    } catch (error: any) {
      console.group('❌ [Bill Save] Error Details')
      console.error('Failed to save bill', error)
      console.error('Error Response:', error.response)
      console.error('Error Response Data:', error.response?.data)
      console.error('Error Message:', error.response?.data?.message || error.message)
      console.error('Error Stack:', error.stack)
      console.groupEnd()
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  // Prepare options
  const vendorOptions: SearchableSelectOption[] = vendors.map((vendor) => ({
    value: String(vendor.vendorId || vendor.id || 0),
    label: vendor.vendorName || vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId || vendor.id}`,
  }))

  const projectOptions: SearchableSelectOption[] = [
    { value: '0', label: t('common.none') },
    ...projects.map((project) => ({
      value: String(project.projectId || project.id || 0),
      label: project.projectName || project.projectNameEn || project.projectNameAr || `Project ${project.projectId || project.id}`,
    })),
  ]

  const itemOptions: SearchableSelectOption[] = items.map((item) => ({
    value: String(item.itemId || item.id || 0),
    label: item.itemName || item.itemNameEn || item.itemNameAr || item.itemCode || `Item ${item.itemId || item.id}`,
  }))

  const taxRateOptions: SearchableSelectOption[] = taxRates.map((tax) => ({
    value: String(tax.taxRateId || tax.id || 0),
    label: `${tax.taxName || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((account) => {
    const accountId = account.accountId || account.id || 0
    const accountCode = account.accountCode || ''
    const accountName = account.accountName || account.accountNameEn || account.accountNameAr || ''
    const displayLabel = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(accountId),
      label: displayLabel,
    }
  })

  // Log account options for debugging
  useEffect(() => {
    if (accounts.length > 0) {
      const options = accounts.map((account) => {
        const accountId = account.accountId || account.id || 0
        const accountCode = account.accountCode || ''
        const accountName = account.accountName || account.accountNameEn || account.accountNameAr || ''
        const displayLabel = accountCode ? `${accountCode} (${accountName})` : accountName
        return {
          value: String(accountId),
          label: displayLabel,
        }
      })
      console.log('🔵 [BillsDetail] Accounts loaded:', accounts.length)
      console.log('🔵 [BillsDetail] Account Options:', options.length)
      console.log('🔵 [BillsDetail] First 3 accounts:', accounts.slice(0, 3))
      console.log('🔵 [BillsDetail] First 3 account options:', options.slice(0, 3))
    } else {
      console.warn('⚠️ [BillsDetail] No accounts loaded')
    }
  }, [accounts])

  const costLocationOptions: SearchableSelectOption[] = costLocations.map((location) => {
    const locationId = location.id || location.costLocationId || 0
    const locationName = location.lookupNameEn || location.lookupNameAr || location.locationName || location.locationNameEn || location.locationNameAr || `Location ${locationId}`
    return {
      value: String(locationId),
      label: locationName,
    }
  })

  const profitCenterOptions: SearchableSelectOption[] = profitCenters.map((center) => {
    const centerId = center.id || center.profitCenterId || 0
    const centerName = center.lookupNameEn || center.lookupNameAr || center.profitCenterName || center.profitCenterNameEn || center.profitCenterNameAr || `Center ${centerId}`
    return {
      value: String(centerId),
      label: centerName,
    }
  })

  const departmentOptions: SearchableSelectOption[] = departments.map((dept) => {
    const deptId = dept.id || dept.departmentId || 0
    const deptName = dept.lookupNameEn || dept.lookupNameAr || dept.departmentName || dept.departmentNameEn || dept.departmentNameAr || `Department ${deptId}`
    return {
      value: String(deptId),
      label: deptName,
    }
  })

  if (loading && !bill.billLines?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditMode ? 'Edit Purchase Bill' : 'New Purchase Bill'}
        description={isEditMode ? 'Update bill details' : 'Create a new purchase bill'}
      />

      {/* Bill Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('bills.billInfo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>{t('bills.accountingDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(bill.billDate)}
                onChange={(e) => setBill((prev) => ({ ...prev, billDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.billTo')}</Label>
              <SearchableSelect
                options={vendorOptions}
                value={bill.vendorId ? String(bill.vendorId) : ''}
                onValueChange={handleVendorChange}
                placeholder={t('bills.selectVendor')}
                searchPlaceholder={t('bills.searchVendors')}
                emptyMessage={t('bills.noVendorsFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.destinationAccountCredit')}</Label>
              <SearchableSelect
                options={accountOptions}
                value={destinationAccount ? String(destinationAccount) : ''}
                onValueChange={(value) => {
                  const accountId = Number(value) || 0
                  setDestinationAccount(accountId)
                }}
                placeholder={t('bills.selectCreditAccount')}
                searchPlaceholder={t('bills.searchAccounts')}
                emptyMessage={t('bills.noAccountsFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.vendorBillDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(bill.vendorBillDate)}
                onChange={(e) => setBill((prev) => ({ ...prev, vendorBillDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.dueDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(bill.dueDate)}
                onChange={(e) => setBill((prev) => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.project')}</Label>
              <SearchableSelect
                options={projectOptions}
                value={bill.projectId ? String(bill.projectId) : '0'}
                onValueChange={(value) => setBill((prev) => ({ ...prev, projectId: Number(value) || 0 }))}
                placeholder={t('bills.selectProject')}
                searchPlaceholder={t('bills.searchProjects')}
                emptyMessage={t('bills.noProjectsFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.file')}</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  {t('common.chooseFile')}
                </Button>
                <Button variant="outline" size="sm">
                  {t('common.browse')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Section */}
      <Card>
        <CardHeader className="bg-primary/5">
          <div className="flex items-center justify-between">
            <CardTitle>Items</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleAddRows(1)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Row
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(3)}>
                <Plus className="w-4 h-4 mr-1" />
                Add 3 Rows
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(5)}>
                <Plus className="w-4 h-4 mr-1" />
                Add 5 Rows
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.item')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.costCenter')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.location')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.department')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.descriptionRequired')} <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle min-w-[250px]">{t('bills.debitAccount')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.qty')} <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.unitPrice')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.itemPrice')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.discPercent')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.discAmount')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.taxable')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.taxRate')} <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.taxAmount')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('bills.amount')}</th>
                  <th className="text-left p-3 font-semibold align-middle w-20"></th>
                </tr>
              </thead>
              <tbody>
                {bill.billLines?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  return (
                    <tr
                      key={index}
                      className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={itemOptions}
                            value={line.itemId ? String(line.itemId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'itemId', Number(value) || 0)}
                            placeholder="Select item"
                            searchPlaceholder="Search items..."
                            emptyMessage="No items found"
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={profitCenterOptions}
                            value={line.costProfitCenterId ? String(line.costProfitCenterId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'costProfitCenterId', Number(value) || 0)}
                            placeholder={t('bills.selectCenter')}
                            searchPlaceholder={t('bills.searchCenters')}
                            emptyMessage={t('bills.noCentersFound')}
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={costLocationOptions}
                            value={line.costLocationId ? String(line.costLocationId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'costLocationId', Number(value) || 0)}
                            placeholder={t('bills.selectLocation')}
                            searchPlaceholder={t('bills.searchLocations')}
                            emptyMessage={t('bills.noLocationsFound')}
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={departmentOptions}
                            value={line.departmentId ? String(line.departmentId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'departmentId', Number(value) || 0)}
                            placeholder="Select department"
                            searchPlaceholder="Search departments..."
                            emptyMessage="No departments found"
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            value={line.description || ''}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            placeholder={t('bills.description')}
                            disabled={saving || isDeleted}
                            className="min-w-[200px]"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10 min-w-[250px]">
                          <SearchableSelect
                            options={accountOptions}
                            value={line.debitAccount ? String(line.debitAccount) : ''}
                            onValueChange={(value) => handleLineChange(index, 'debitAccount', Number(value) || 0)}
                            placeholder="Select account"
                            searchPlaceholder="Search accounts..."
                            emptyMessage="No accounts found"
                            disabled={saving || isDeleted}
                            className="w-full min-w-[250px]"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.qty || ''}
                            onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                            placeholder="0"
                            disabled={saving || isDeleted}
                            className="w-20"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice || ''}
                            onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted}
                            className="w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.itemPrice?.toFixed(2) || '0.00'}
                          readOnly
                          className="bg-muted w-24"
                        />
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.discountPercent || ''}
                            onChange={(e) => handleLineChange(index, 'discountPercent', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted}
                            className="w-20"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.discountAmount?.toFixed(2) || '0.00'}
                          readOnly
                          className="bg-muted w-24"
                        />
                      </td>
                      <td className="p-3 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.taxableAmount?.toFixed(2) || '0.00'}
                          readOnly
                          className="bg-muted w-24"
                        />
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={taxRateOptions}
                            value={line.taxRateId ? String(line.taxRateId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value) || 0)}
                            placeholder={t('bills.selectTax')}
                            searchPlaceholder={t('bills.searchTaxRates')}
                            emptyMessage={t('bills.noTaxRatesFound')}
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.taxAmount?.toFixed(2) || '0.00'}
                          readOnly
                          className="bg-muted w-24"
                        />
                      </td>
                      <td className="p-3 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.lineAmount?.toFixed(2) || '0.00'}
                          readOnly
                          className="bg-muted w-24"
                        />
                      </td>
                      <td className="p-3 align-middle">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving}
                          className={
                            isDeleted
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                              : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                          }
                          title={isDeleted ? 'Undo' : 'Delete Row'}
                        >
                          {isDeleted ? <span className="text-xs font-semibold">Undo</span> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="mt-6 bg-primary/5 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">{t('bills.subTotal')}</Label>
                <p className="text-lg font-semibold">{totals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.totalDisc')}</Label>
                <p className="text-lg font-semibold">{totals.totalDiscount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.taxableAmount')}</Label>
                <p className="text-lg font-semibold">{totals.taxableAmount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('bills.totalTax')}</Label>
                <p className="text-lg font-semibold">{totals.totalTax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('bills.netAmount')}</Label>
                <p className="text-lg font-semibold">{totals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('bills.notesRemarks')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={bill.note || ''}
            onChange={(e) => setBill((prev) => ({ ...prev, note: e.target.value }))}
            placeholder={t('bills.enterNotesRemarks')}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/purchases/bills')} disabled={saving} className="w-full sm:w-auto">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('bills.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('bills.updateBill') : t('bills.saveBill')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

